import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { gbp, monthLabel, ProfitPill, fmtDate } from '@/components/training/analytics/shared';
import { STREAM_COLOURS } from './OverviewCards';

export async function put(path, body) {
  const res = await fetch(`${API_URL}/api/staff/business-overview/${path}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
}

export function MoneyInput({ value, onSave, testid, placeholder = '0.00', allowClear = false }) {
  const [v, setV] = useState(value === null || value === undefined || value === 0 ? '' : String(value));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const num = v === '' ? (allowClear ? null : 0) : Number(v);
    if (num !== null && (Number.isNaN(num) || num < 0)) { toast.error('Enter a valid amount'); return; }
    if ((num ?? 0) === Number(value ?? 0) && !(allowClear && num === null && value !== null && value !== undefined)) return;
    setBusy(true);
    try { await onSave(num); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-gray-400">£</span>
      <input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && e.target.blur()} placeholder={placeholder}
        className="w-24 h-7 px-1.5 text-right text-sm border border-gray-200 rounded bg-white focus:border-emerald-500 focus:outline-none tabular-nums" data-testid={testid} />
      {busy && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
    </span>
  );
}

/** Monthly costs per stream — only rendered when the user has profit access. */
export function MonthlyCostsTable({ data, reload }) {
  const streams = data.filters.all_streams.filter((s) => data.filters.streams.includes(s.key));
  return (
    <Card className="p-0 overflow-hidden" data-testid="monthly-costs-table">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h2 className="font-bold text-gray-900">Monthly costs by part of the business</h2>
        <p className="text-[11px] text-gray-500 mt-0.5">Type costs that aren't tied to a single order (ads, fuel, wages…) — saves when you leave the box. Training marketing/other costs come from Training Analytics and are already included.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-gray-500 border-b">
            <tr><th className="text-left px-4 py-2">Month</th><th className="text-right px-3 py-2">Revenue</th>{streams.map((s) => <th key={s.key} className="text-right px-3 py-2" style={{ color: STREAM_COLOURS[s.key] }}>{s.label}</th>)}<th className="text-right px-3 py-2">All costs</th><th className="text-right px-4 py-2">Profit</th></tr>
          </thead>
          <tbody className="divide-y">
            {data.months.map((m) => (
              <tr key={m.month} className="hover:bg-gray-50/60" data-testid={`overview-month-${m.month}`}>
                <td className="px-4 py-2 font-semibold text-gray-900 whitespace-nowrap">{monthLabel(m.month)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{gbp(m.total)}</td>
                {streams.map((s) => (
                  <td key={s.key} className="px-3 py-2 text-right">
                    <MoneyInput value={data.monthly_costs?.[`${m.month}|${s.key}`]?.cost} testid={`stream-cost-${s.key}-${m.month}`}
                      onSave={async (num) => { await put('costs', { month: m.month, stream: s.key, cost: num ?? 0 }); reload(); }} />
                  </td>
                ))}
                <td className="px-3 py-2 text-right tabular-nums text-gray-700">{gbp(m.cost)}</td>
                <td className="px-4 py-2 text-right"><ProfitPill value={m.profit} testid={`overview-month-profit-${m.month}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function OrdersTable({ data, reload }) {
  const [showAll, setShowAll] = useState(false);
  const [onlyUncosted, setOnlyUncosted] = useState(false);
  const canProfit = data.can_view_profit;
  let rows = data.orders;
  if (canProfit && onlyUncosted) rows = rows.filter((o) => o.editable_cost && o.manual_cost === null && !o.cost_complete);
  const shown = showAll ? rows : rows.slice(0, 25);
  return (
    <Card className="p-0 overflow-hidden" data-testid="orders-table">
      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap items-center gap-3">
        <div><h2 className="font-bold text-gray-900">Sales in this window</h2><p className="text-[11px] text-gray-500">{canProfit ? 'Cost comes from stock cost prices; type a real cost to override it when stock prices don\'t cover it.' : 'Revenue per sale · ex VAT'}</p></div>
        {canProfit && (
          <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={onlyUncosted} onChange={(e) => setOnlyUncosted(e.target.checked)} data-testid="orders-only-uncosted" /> Only sales missing a cost</label>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-gray-500 border-b">
            <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-3 py-2">Sale</th><th className="text-left px-3 py-2">Customer</th><th className="text-right px-3 py-2">Revenue</th>{canProfit && <><th className="text-right px-3 py-2">Cost</th><th className="text-right px-4 py-2">Profit</th></>}</tr>
          </thead>
          <tbody className="divide-y">
            {shown.length === 0 && <tr><td colSpan={canProfit ? 6 : 4} className="px-4 py-6 text-sm text-gray-500 italic">Nothing here.</td></tr>}
            {shown.map((o) => (
              <tr key={`${o.stream}-${o.id}`} className="hover:bg-gray-50/60" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-2 whitespace-nowrap text-gray-700">{fmtDate(o.date)}</td>
                <td className="px-3 py-2"><span className="font-semibold text-gray-900">{o.reference}</span> <span className="text-[10px] uppercase tracking-wide font-bold" style={{ color: STREAM_COLOURS[o.stream] }}>{o.stream_label}</span> <span className="text-[10px] text-gray-400">{o.brand_label}</span></td>
                <td className="px-3 py-2 text-gray-700 truncate max-w-[220px]">{o.customer || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{gbp(o.revenue)}</td>
                {canProfit && (
                  <>
                    <td className="px-3 py-2 text-right">
                      {o.editable_cost ? (
                        <div className="inline-flex flex-col items-end">
                          <MoneyInput value={o.manual_cost} allowClear placeholder={o.cost_auto ? o.cost_auto.toFixed(2) : 'add cost'} testid={`order-cost-${o.id}`}
                            onSave={async (num) => { await put('order-cost', { stream: o.stream, order_id: o.id, cost: num }); reload(); }} />
                          <span className={`text-[10px] ${o.manual_cost !== null ? 'text-violet-700' : o.cost_complete ? 'text-gray-400' : 'text-amber-700'}`}>
                            {o.manual_cost !== null ? 'manual' : o.cost_complete ? 'from stock prices' : 'stock cost missing'}
                          </span>
                        </div>
                      ) : <span className="tabular-nums text-gray-700">{gbp(o.cost)}</span>}
                    </td>
                    <td className="px-4 py-2 text-right"><ProfitPill value={o.profit} /></td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 25 && <button type="button" onClick={() => setShowAll((s) => !s)} className="m-3 text-xs text-emerald-700 hover:underline" data-testid="orders-toggle">{showAll ? 'Show fewer' : `Show all ${rows.length}`}</button>}
    </Card>
  );
}
